/* ==========================================================================
   THEME JS — vanilla custom elements, no dependencies.

   Rules this file follows:
   - Hover is a progressive enhancement; every interaction has a click or
     keyboard path. Nothing opens on hover alone.
   - Anything that animates checks prefers-reduced-motion.
   - State that CSS reads (is-open, aria-expanded) is set here, once.
   ========================================================================== */

(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapable = window.matchMedia('(hover: hover)');
  const desktop = window.matchMedia('(min-width: 768px)');

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  /* ------------------------------------------------------------------------
     Header — scroll state, live --header-height, panel coordination
     ------------------------------------------------------------------------ */

  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.openPanel = null;
      this.hoverTimer = null;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);

      this.publishHeight();
      this.onScroll();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.publishHeight());
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', () => this.publishHeight());
      }

      this.bindToggles();
      this.bindToolbar();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.resizeObserver?.disconnect();
    }

    /* Sticky overlays elsewhere on the page offset against this. Without it
       they slide under the header. */
    publishHeight() {
      const height = Math.round(this.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }

    onScroll() {
      this.classList.toggle('is-scrolled', window.scrollY > 8);
      this.updateSymbolRotation();
    }

    /* Rotation tracks scroll position directly (no rAF throttle needed —
       it's a single custom-property write, cheap even at native scroll
       event frequency). Uncapped: unlike the old grow-on-scroll version,
       this never settles, so the icon keeps turning the further you
       scroll. Skipped under reduced motion, matching the CSS fallback. */
    updateSymbolRotation() {
      if (reduceMotion.matches) return;
      this.style.setProperty('--logo-rotate', `${(window.scrollY * 0.5).toFixed(1)}deg`);
    }

    bindToggles() {
      this.toggles = Array.from(this.querySelectorAll('[data-panel-toggle]'));

      this.toggles.forEach((toggle) => {
        const panel = this.querySelector(`#${toggle.getAttribute('aria-controls')}`);
        if (!panel) return;

        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          this.togglePanel(toggle, panel);
        });

        // Pointer intent: open on hover, but only as an accelerator. The click
        // handler above remains the real control, so keyboard works.
        if (hoverCapable.matches) {
          const open = () => {
            if (!desktop.matches) return;
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => this.openPanelFor(toggle, panel), 80);
          };
          const close = () => {
            clearTimeout(this.hoverTimer);
            this.hoverTimer = setTimeout(() => {
              if (!panel.matches(':hover') && !toggle.matches(':hover')) this.closePanels();
            }, 160);
          };

          toggle.addEventListener('mouseenter', open);
          toggle.addEventListener('mouseleave', close);
          panel.addEventListener('mouseenter', () => clearTimeout(this.hoverTimer));
          panel.addEventListener('mouseleave', close);
        }
      });
    }

    togglePanel(toggle, panel) {
      const isOpen = toggle.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.closePanels();
      } else {
        this.openPanelFor(toggle, panel);
      }
    }

    openPanelFor(toggle, panel) {
      if (this.openPanel === panel) return;
      this.closePanels();

      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this.openPanel = panel;
      this.openToggle = toggle;

      this.classList.add('has-open-panel');
      toggle.closest('.header-menu')?.classList.add('has-open');
    }

    closePanels({ restoreFocus = false } = {}) {
      this.toggles?.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        const panel = this.querySelector(`#${toggle.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = true;
      });

      this.classList.remove('has-open-panel');
      this.querySelector('.header-menu')?.classList.remove('has-open');

      if (restoreFocus && this.openToggle) this.openToggle.focus();
      this.openPanel = null;
      this.openToggle = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.openPanel) {
        this.closePanels({ restoreFocus: true });
      }
    }

    onDocumentClick(event) {
      if (this.openPanel && !this.contains(event.target)) this.closePanels();
    }

    /* Toolbar dimming is a visual echo of aria-expanded, never the only cue. */
    bindToolbar() {
      const toolbar = this.querySelector('.site-header__misc');
      if (!toolbar) return;

      toolbar.addEventListener('click', (event) => {
        const link = event.target.closest('.site-header__misc-link');
        toolbar.querySelectorAll('.site-header__misc-link').forEach((el) => {
          el.classList.toggle('is-active', el === link);
        });
        toolbar.classList.toggle('has-active', Boolean(link));
      });
    }
  }

  /* ------------------------------------------------------------------------
     Drawer — focus trap, Escape, restore focus, scroll lock
     ------------------------------------------------------------------------ */

  class ThemeDrawer extends HTMLElement {
    connectedCallback() {
      this.trigger = null;
      this.onKeydown = this.onKeydown.bind(this);

      this.querySelectorAll('[data-drawer-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );

      document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((el) =>
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(el);
        })
      );
    }

    open(trigger) {
      this.trigger = trigger || null;
      this.hidden = false;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);

      const first = this.querySelector(FOCUSABLE);
      requestAnimationFrame(() => first?.focus());

      this.trigger?.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.hidden = true;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);

      this.trigger?.setAttribute('aria-expanded', 'false');
      this.trigger?.focus();
      this.trigger = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* ------------------------------------------------------------------------
     Tabs — real APG pattern. The reference build used plain buttons with
     visibility:hidden panels, which left hidden links in the tab order.
     ------------------------------------------------------------------------ */

  class ThemeTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });

      this.select(Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')));
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      // hidden on the panel itself — it leaves the a11y tree and the tab order
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }

    onKeydown(event, index) {
      const keys = {
        ArrowRight: index + 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: this.tabs.length - 1
      };

      if (!(event.key in keys)) return;
      event.preventDefault();

      const next = (keys[event.key] + this.tabs.length) % this.tabs.length;
      this.select(next);
      this.tabs[next].focus();
    }
  }

  /* ------------------------------------------------------------------------
     Slider — scroll-snap driven, pagination reflects real scroll position
     ------------------------------------------------------------------------ */

  class ThemeSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slider__track');
      this.slides = Array.from(this.querySelectorAll('.slider__slide'));
      this.dots = Array.from(this.querySelectorAll('.slider__dot'));
      if (!this.track) return;

      this.index = 0;
      this.autoplayDelay = Number(this.dataset.autoplay || 0) * 1000;
      if (this.autoplayDelay > 0) this.initAutoplay();

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.goTo(index));
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const index = this.slides.indexOf(entry.target);
              this.setCurrent(index);
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    }

    /* Autoplay. The active slide's CTA rule doubles as the progress bar — it
       fills over exactly the autoplay delay, so the animation IS the timer
       made visible. Never runs under reduced motion: a bar that animates
       continuously is precisely the kind of motion that setting asks us to
       stop, and an un-announced auto-advance is disorienting besides. */
    initAutoplay() {
      this.style.setProperty('--hero-autoplay', `${this.autoplayDelay}ms`);

      const start = () => {
        if (reduceMotion.matches || this.timer) return;
        this.classList.add('is-autoplaying');
        this.timer = setInterval(() => this.advance(), this.autoplayDelay);
      };
      const stop = () => {
        clearInterval(this.timer);
        this.timer = null;
        this.classList.remove('is-autoplaying');
      };

      this.startAutoplay = start;
      this.stopAutoplay = stop;

      // Pause while the visitor is engaging with it, or the tab is hidden.
      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      this.track.addEventListener('pointerdown', stop);
      document.addEventListener('visibilitychange', () => {
        document.hidden ? stop() : start();
      });
      reduceMotion.addEventListener?.('change', () => {
        reduceMotion.matches ? stop() : start();
      });

      start();
    }

    advance() {
      const next = (this.index + 1) % this.slides.length;
      this.goTo(next);
    }

    /* Scrolls only the track's own horizontal scrollbar — never the page.
       `element.scrollIntoView()` looks like the obvious tool here, but its
       `block` option walks every scrollable ancestor, including <html>. On
       an autoplaying slider (the hero) that meant the *page* smooth-scrolled
       back up to the hero every time the timer advanced, regardless of how
       far down the visitor had since scrolled — the browser was "helpfully"
       bringing the next slide back into vertical view. Setting the track's
       own scrollLeft directly cannot leak into the page's scroll position. */
    goTo(index) {
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      this.track.scrollTo({
        left: slide.offsetLeft - this.track.offsetLeft,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
    }

    /* Restart the progress fill from zero on every slide change, otherwise it
       would carry the previous slide's partially-run animation. */
    restartProgress() {
      const active = this.querySelector('[data-caption-index].is-active .cta');
      if (!active) return;
      active.style.animation = 'none';
      void active.offsetWidth;
      active.style.animation = '';
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearInterval(this.timer);
    }

    setCurrent(index) {
      this.index = index;
      // Only the pagination reflects position. Slides are never aria-hidden:
      // they stay in the DOM and scrollable, and a multi-up view (the hero
      // slider shows two panels at once) has more than one visible at a time,
      // so hiding "non-current" slides would hide real, on-screen content.
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));

      // Captions live outside the scrolling track (so the text doesn't slide
      // away mid-swipe), which means they have to be switched here.
      if (!this.captions) {
        this.captions = Array.from(this.querySelectorAll('[data-caption-index]'));
      }
      this.captions.forEach((caption, i) => {
        const active = i === index;
        caption.classList.toggle('is-active', active);
        if (active) {
          caption.removeAttribute('aria-hidden');
        } else {
          caption.setAttribute('aria-hidden', 'true');
        }
      });

      this.restartProgress?.();
    }
  }

  /* ------------------------------------------------------------------------
     Horizontal rail — keyboard scrolling
     ------------------------------------------------------------------------ */

  class ThemeRail extends HTMLElement {
    connectedCallback() {
      this.rails = Array.from(this.querySelectorAll('[data-rail]'));
      this.rails.forEach((rail) => {
        rail.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          const card = rail.querySelector(':scope > *');
          if (!card) return;
          event.preventDefault();
          const step = card.getBoundingClientRect().width + 16;
          rail.scrollBy({
            left: event.key === 'ArrowRight' ? step : -step,
            behavior: reduceMotion.matches ? 'auto' : 'smooth'
          });
        });
      });
    }
  }

  /* ------------------------------------------------------------------------
     Announcement bar — autoplay that actually stops for reduced motion.
     CSS alone cannot halt a JS timer, which is why the reference build's
     looping bar kept running.
     ------------------------------------------------------------------------ */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('.announcement-bar__message'));
      if (this.messages.length <= 1) return;

      this.index = 0;
      this.delay = Number(this.dataset.delay || 5000);

      this.start();

      // Pause while hovered or focused so a reader is never cut off mid-line.
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.stop() : this.start();
      });

      reduceMotion.addEventListener?.('change', () => {
        reduceMotion.matches ? this.stop() : this.start();
      });
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      if (reduceMotion.matches || this.timer) return;
      this.timer = setInterval(() => this.advance(), this.delay);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      const current = this.messages[this.index];
      this.index = (this.index + 1) % this.messages.length;
      const next = this.messages[this.index];

      current.classList.add('is-leaving');
      current.classList.remove('is-active');
      setTimeout(() => current.classList.remove('is-leaving'), 500);
      next.classList.add('is-active');
    }
  }

  /* ------------------------------------------------------------------------
     Quantity input
     ------------------------------------------------------------------------ */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', () => {
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = Number(this.input.min || 0);
          const next = Math.max(min, Number(this.input.value) + step);
          this.input.value = String(next);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  /* ------------------------------------------------------------------------
     Product form — variant selection + add to cart with live announcement
     ------------------------------------------------------------------------ */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-cart-status]');
      this.variantInput = this.querySelector('[name="id"]');

      this.variants = this.parseVariants();

      this.querySelectorAll('.variant-option__input').forEach((input) =>
        input.addEventListener('change', () => this.onVariantChange())
      );

      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    }

    parseVariants() {
      const script = this.querySelector('[data-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('.variant-option__input:checked')).map(
        (input) => input.value
      );
    }

    onVariantChange() {
      const selected = this.selectedOptions();
      const match = this.variants.find((variant) =>
        variant.options.every((option, index) => option === selected[index])
      );

      if (!match) {
        this.setUnavailable();
        return;
      }

      this.variantInput.value = match.id;

      const priceTarget = this.querySelector('[data-variant-price]');
      if (priceTarget && match.price_formatted) priceTarget.innerHTML = match.price_formatted;

      this.submitButton.disabled = !match.available;
      this.submitButton.textContent = match.available
        ? this.submitButton.dataset.labelAvailable
        : this.submitButton.dataset.labelSoldOut;

      if (match.featured_media_id) {
        const media = this.closest('.product')?.querySelector(
          `[data-media-id="${match.featured_media_id}"]`
        );
        media?.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'nearest'
        });
      }

      // Update the URL so a shared link lands on the same variant.
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    }

    setUnavailable() {
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.submitButton.dataset.labelUnavailable;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.disabled) return;

      this.submitButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(this.variantInput.value),
                quantity: Number(this.querySelector('[name="quantity"]')?.value || 1)
              }
            ]
          })
        });

        const data = await response.json();

        if (!response.ok) throw new Error(data.description || data.message);

        this.announce(this.dataset.addedMessage?.replace('[title]', data.items?.[0]?.title || ''));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { openDrawer: true } }));
      } catch (error) {
        this.announce(error.message, true);
      } finally {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    announce(message, isError = false) {
      if (!this.status || !message) return;
      this.status.textContent = message;
      this.status.classList.toggle('form-message--error', isError);
      this.status.classList.toggle('form-message--success', !isError);
    }
  }

  /* ------------------------------------------------------------------------
     Auto-submitting controls (sort, localization, cart quantity).
     Bound here rather than with inline onchange so the theme stays free of
     inline event handlers and works under a strict CSP. Every one of these
     has a <noscript> submit button as a fallback.
     ------------------------------------------------------------------------ */

  document.addEventListener('change', (event) => {
    const control = event.target.closest('[data-auto-submit]');
    if (control) control.form?.submit();
  });

  /* ------------------------------------------------------------------------
     Generic disclosure. Any [data-collapse-toggle] with aria-controls toggles
     its target's hidden state and keeps aria-expanded truthful.
     ------------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-collapse-toggle]');
    if (!toggle) return;

    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;

    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!isOpen));
    target.hidden = isOpen;
  });

  /* ------------------------------------------------------------------------
     Money formatting — Shopify's money_format setting is a template string
     like "₹{{amount}}" or "Rs. {{amount_with_comma_separator}}", not a
     currency code, so formatting has to walk the same placeholders Shopify's
     own {{ | money }} filter does. cart.js returns integer paise, never a
     pre-formatted string.
     ------------------------------------------------------------------------ */

  function formatMoney(cents) {
    const format = window.theme?.moneyFormat || '₹{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');

    const withCommas = (n) => n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Indian digit grouping (lakh/crore): last 3 digits, then pairs of 2.
    const withIndianCommas = (n) => {
      if (n.length <= 3) return n;
      const last3 = n.slice(-3);
      const rest = n.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
      return `${rest},${last3}`;
    };

    const replacements = {
      amount: `${withIndianCommas(whole)}.${decimal}`,
      amount_no_decimals: withIndianCommas(whole),
      amount_with_comma_separator: `${withCommas(whole)},${decimal}`,
      amount_no_decimals_with_comma_separator: withCommas(whole),
      amount_with_apostrophe_separator: `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${decimal}`
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => replacements[token] ?? match);
  }

  /* ------------------------------------------------------------------------
     Cart drawer — renders live from /cart.js so it can never show a stale
     cart, handles its own quantity/remove interactions, and opens itself
     after a successful add-to-cart.
     ------------------------------------------------------------------------ */

  const cartDrawer = {
    el: null,
    body: null,
    footer: null,
    subtotalEl: null,

    init() {
      this.el = document.getElementById('CartDrawer');
      if (!this.el) return;
      this.body = this.el.querySelector('[data-cart-drawer-body]');
      this.footer = this.el.querySelector('[data-cart-drawer-footer]');
      this.subtotalEl = this.el.querySelector('[data-cart-drawer-subtotal]');

      this.body.addEventListener('click', (event) => this.onClick(event));
      this.body.addEventListener('change', (event) => this.onQuantityInput(event));
    },

    lineTemplate(item, index) {
      const image = item.image
        ? `<img src="${item.image}&width=200" width="96" height="128" alt="" loading="lazy">`
        : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="text-12-book text-light">${item.variant_title}</p>`
        : '';

      return `
        <div class="cart__line" data-cart-line="${index}">
          <a class="cart__line-media image-container" href="${item.url}" tabindex="-1" aria-hidden="true">${image}</a>
          <div class="cart__line-details">
            <div class="flex justify-between gap-16">
              <a class="text-18-display" href="${item.url}">${item.product_title}</a>
              <p class="text-14-book">${formatMoney(item.final_line_price)}</p>
            </div>
            ${variant}
            <div class="flex items-center justify-between gap-16">
              <quantity-input class="quantity">
                <button type="button" class="quantity__button button-reset" data-action="decrease" aria-label="${window.theme?.strings?.decreaseQuantity || 'Decrease quantity'}">−</button>
                <label class="visually-hidden" for="CartDrawerQty-${index}">Quantity</label>
                <input class="quantity__input" id="CartDrawerQty-${index}" type="number" min="0" value="${item.quantity}" data-cart-quantity data-line="${index}">
                <button type="button" class="quantity__button button-reset" data-action="increase" aria-label="${window.theme?.strings?.increaseQuantity || 'Increase quantity'}">+</button>
              </quantity-input>
              <button type="button" class="text-11-cta button-reset" data-cart-remove data-line="${index}">
                ${window.theme?.strings?.remove || 'Remove'}
              </button>
            </div>
          </div>
        </div>`;
    },

    render(cart) {
      if (!this.body) return;

      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="empty-state">
            <p class="text-14-book text-light">${window.theme?.strings?.cartEmpty || 'Your basket is empty'}</p>
          </div>`;
        this.footer.hidden = true;
        return;
      }

      this.body.innerHTML = cart.items.map((item, index) => this.lineTemplate(item, index)).join('');
      this.subtotalEl.textContent = formatMoney(cart.total_price);
      this.footer.hidden = false;
    },

    async refresh() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await response.json();
      this.render(cart);
      this.syncCount(cart);
      return cart;
    },

    syncCount(cart) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
      // Show/hide the badge bubble so an empty cart carries no count.
      document.querySelectorAll('.site-header__cart-count').forEach((el) => {
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
    },

    async changeLine(line, quantity) {
      this.body.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line + 1, quantity })
        });
        const cart = await response.json();
        this.render(cart);
        this.syncCount(cart);
      } catch (error) {
        /* Leave the drawer showing its last known-good state. */
      } finally {
        this.body.removeAttribute('aria-busy');
      }
    },

    onClick(event) {
      const removeBtn = event.target.closest('[data-cart-remove]');
      if (removeBtn) this.changeLine(Number(removeBtn.dataset.line), 0);
    },

    // The +/- buttons are handled entirely by <quantity-input> (the same
    // element the standalone cart page and product form use) — it updates
    // the input's value and dispatches 'change' itself. This listener is
    // the single place that turns any change in quantity, however it
    // happened, into the actual cart/change.js call.
    onQuantityInput(event) {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      this.changeLine(Number(input.dataset.line), Math.max(0, Number(input.value)));
    }
  };

  document.addEventListener('DOMContentLoaded', () => cartDrawer.init());
  if (document.readyState !== 'loading') cartDrawer.init();

  document.addEventListener('cart:updated', async (event) => {
    const cart = await cartDrawer.refresh().catch(() => null);
    // cartDrawer.el is the <theme-drawer> custom element instance, so its
    // own open()/close() methods are directly callable — no separate event
    // plumbing needed to reuse the same focus-trap/backdrop/Escape behaviour
    // the header's basket link and the mobile menu button already get.
    if (cart && event.detail?.openDrawer && cartDrawer.el.hidden) {
      cartDrawer.el.open();
    }
  });

  /* ------------------------------------------------------------------------
     Registration
     ------------------------------------------------------------------------ */

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  define('site-header', SiteHeader);
  define('theme-drawer', ThemeDrawer);
  define('theme-tabs', ThemeTabs);
  define('theme-slider', ThemeSlider);
  define('theme-rail', ThemeRail);
  define('announcement-bar', AnnouncementBar);
  define('quantity-input', QuantityInput);
  define('product-form', ProductForm);
})();
